<template>
  <m-page :loading="loading">
    <template #header>
      <div class="flex flex-col items-stretch border-b border-primary bg-white">
        <template v-if="!error && customerStore.studio">
          <m-customer-menu />
          <m-shopping-cart-bar v-if="showShoppingCartBar" />
        </template>
      </div>
    </template>

    <m-container class="p-2" layout="grow">
      <m-transitioned-router-view v-if="!error" />

      <m-card-info
        v-else
        :icon="faExclamationTriangle"
        title="No Studio Found."
        detail="Please check the URL and try again."
      />
    </m-container>

    <template #footer>
      <m-footer />
    </template>
  </m-page>
</template>
<script lang="ts" setup>
  import { onMounted, onUnmounted, watch, computed } from 'vue';
  import { faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
  import type { RouteLocationNormalized, RouteLocationRaw, RouteParamsRaw } from 'vue-router';
  import { onBeforeRouteUpdate, useRoute, useRouter } from 'vue-router';
  import { useTitle } from '@vueuse/core';
  import { storeToRefs } from 'pinia';
  import { useHead } from 'unhead';
  import MCustomerMenu from '@shared/components/MCustomerMenu/MCustomerMenu.vue';
  import MFooter from '@core/components/MFooter/MFooter.vue';
  import { useAsyncResult } from '@shared/composables/useAsync';
  import { parseRouteId } from '@shared/composables/useRouteIds';
  import MTransitionedRouterView from '@shared/components/MRouter/MTransitionedRouterView.vue';
  import { useFeaturesStore } from '@shared/stores/features.store';
  import MShoppingCartBar from '@shared/components/MShoppingCartBar/MShoppingCartBar.vue';
  import { SELECT_PACKAGE_DETAILS_PAGE, SELECT_PACKAGE_PAGE } from '@apps/customer-package-builder/routing';
  import { CUSTOMER_CART_PAGE, CUSTOMER_CART_ORDER_SERVICE_PAGE } from '@apps/cart/routing';
  import MCardInfo from '@shared/components/MCard/MCardInfo.vue';
  import MContainer from '@core/components/MContainer/MContainer.vue';
  import { useCustomerStore } from '../stores/customer.store';
  import { useCartStore } from '../stores/cart.store';
  import { CONFIRM_CART, JOB_UNAVAILABLE, STUDIO_ACCESS_CODE, SUBJECTS_GALLERY, SUBJECT_SEARCH } from '../routing';

  const featureStore = useFeaturesStore();
  const customerStore = useCustomerStore();
  const cartStore = useCartStore();
  const router = useRouter();
  const route = useRoute();

  const { studio } = storeToRefs(customerStore);

  const title = computed(() => {
    let t = 'My Pro Images';
    if (studio?.value) t = `${studio.value.name} - ${t}`;

    return t;
  });
  useTitle(title);
  useHead({
    meta: [
      {
        name: 'robots',
        content: 'none', //Equivalent to noindex, nofollow.
      },
    ],
  });

  const jobId = computed(() => parseRouteId(route.params.jobId));
  const loading = computed(() => loadingCustomer.value || loadingFeatures.value);
  const showShoppingCartBar = computed(() =>
    [
      SUBJECTS_GALLERY,
      SUBJECT_SEARCH,
      SELECT_PACKAGE_PAGE,
      SELECT_PACKAGE_DETAILS_PAGE,
      CUSTOMER_CART_PAGE,
      CUSTOMER_CART_ORDER_SERVICE_PAGE,
    ].includes(route.name?.toString() ?? ''),
  );

  const {
    execute: loadStore,
    result: error,
    loading: loadingCustomer,
  } = useAsyncResult(async () => await customerStore.initialize(route.params.studioSlug as string, jobId.value));

  const { loading: loadingFeatures, execute: loadFeaturesStore } = useAsyncResult(
    async () => await featureStore.initialize(),
    {
      initialLoading: true,
    },
  );

  const validateRoute = (to: RouteLocationNormalized, from?: RouteLocationNormalized): RouteLocationRaw | undefined => {
    const hasPackages = !!cartStore.persistedPackageLength;
    const toJobId = parseRouteId(to.params.jobId);
    const isToJobSearchPage = to.name === STUDIO_ACCESS_CODE;
    const isConfirmCartPage = to.name === CONFIRM_CART;
    const isUnavailablePage = to.name === JOB_UNAVAILABLE;
    const isMisMatchedJobId = toJobId && cartStore.persistedJobId && toJobId != cartStore.persistedJobId;
    const isSubjectSearchPage = to.name === SUBJECT_SEARCH;
    const isToSelectPackagePage = to.name === SELECT_PACKAGE_PAGE;

    if (!isToJobSearchPage && !isConfirmCartPage && !cartStore.persistedOrderMethod) {
      return {
        name: STUDIO_ACCESS_CODE,
        params: {
          code: to.query.code,
        } as RouteParamsRaw,
        query: {
          code: to.query.code,
        },
      };
    }

    if (isToSelectPackagePage && from === undefined && customerStore.isPrePay && !customerStore.hasImportedData) {
      return {
        name: SUBJECT_SEARCH,
      };
    }

    if (isSubjectSearchPage && customerStore.isHosted) {
      return {
        name: STUDIO_ACCESS_CODE,
      };
    }

    if (!isUnavailablePage && !isConfirmCartPage && isMisMatchedJobId && hasPackages) {
      return {
        name: CONFIRM_CART,
        params: {
          jobId: cartStore.persistedJobId,
        },
        query: {
          returnUrl: encodeURIComponent(to.fullPath),
        },
      };
    }
  };

  onMounted(async () => {
    await Promise.all([loadStore(), loadFeaturesStore()]);
    const routeValidation = validateRoute(route);
    if (routeValidation) {
      router.replace(routeValidation);
    }
  });

  onBeforeRouteUpdate((to, from, next) => {
    const routeValidation = validateRoute(to, from);
    if (routeValidation) {
      next(routeValidation);
    } else next();
  });

  onUnmounted(() => {
    customerStore.clear();
    featureStore.clear();
  });

  const initializeStore = async () => await customerStore.initialize(route.params.studioSlug as string, jobId.value);

  watch(() => route.params.studioSlug, initializeStore);
  watch(() => route.params.jobId, initializeStore);
</script>
